import { clientSDK, queryClient } from "@istari-frontend/api-client";
import { NewTrackedFile, NewTrackedSystem, TrackedFileSpecifierType, V3Models } from "@istari/istari-client";
import { useMutation } from "@tanstack/react-query";

import { type ConfigurationTrackedFile } from "~/api/configurations";
import { trackedSystemsQueryOptions } from "~/api/system-tree";
import { useAddConfigTrackedFiles } from "~/api/useSystems";
import { blankNotebookFile, notebookBaseName } from "~/notebook/core/blank-notebook";
import { useCreateResource } from "~/resource/data-source/use-create-resource";

type CreateNotebookParams = {
  displayName: string;
  systemId: string;
  configName: string;
  existingTrackedFiles: ConfigurationTrackedFile[];
  // Source configuration the notebook is added to. Its tracked systems are carried into the new
  // config so creating a notebook never drops the system's subsystems.
  sourceConfigId?: string;
};

type CreateNotebookResult = {
  resourceId: string;
  fileId: string;
  fileRevisionId: string;
  configId: string;
  snapshotId: string | undefined;
};

export function useCreateNotebook() {
  const createResource = useCreateResource();
  const addConfigTrackedFiles = useAddConfigTrackedFiles();

  return useMutation<CreateNotebookResult, unknown, CreateNotebookParams>({
    mutationFn: async ({ displayName, systemId, configName, existingTrackedFiles, sourceConfigId }) => {
      const created = await createResource.mutateAsync({
        file: blankNotebookFile(displayName),
        displayName: notebookBaseName(displayName),
        resourceType: V3Models.ResourceTypeDto.File,
      });

      const existingTrackedSystems = sourceConfigId
        ? ((await queryClient.ensureQueryData(trackedSystemsQueryOptions(sourceConfigId))).items ?? [])
        : [];
      const trackedSystems = existingTrackedSystems.map(
        (ts) => new NewTrackedSystem(ts.childSystemId, ts.specifierType === "Tag" ? ts.pinnedConfigId : undefined),
      );

      const trackedFiles: NewTrackedFile[] = [
        ...existingTrackedFiles.map(
          (tf) =>
            new NewTrackedFile(
              tf.specifierType === TrackedFileSpecifierType.Locked
                ? TrackedFileSpecifierType.Locked
                : TrackedFileSpecifierType.Latest,
              tf.fileId,
              tf.specifierType === TrackedFileSpecifierType.Locked ? tf.pinnedFileRevisionId : undefined,
            ),
        ),
        new NewTrackedFile(TrackedFileSpecifierType.Latest, created.fileId),
      ];

      const newConfig = await addConfigTrackedFiles.mutateAsync({
        name: configName,
        systemId,
        trackedFiles,
        trackedSystems: trackedSystems.length > 0 ? trackedSystems : undefined,
      });

      // Best-effort: the resource + config are already created, so a transient listSnapshots failure
      // shouldn't fail the whole flow and leave an orphan resource. The caller falls back to the
      // config URL when snapshotId is undefined and the page resolves the snapshot itself.
      let newSnapshotId: string | undefined;
      try {
        const snapshots = await clientSDK.listSnapshots(systemId, newConfig.id, null, 1, 1);
        newSnapshotId = snapshots.items[0]?.id;
      } catch {
        newSnapshotId = undefined;
      }

      return {
        resourceId: created.resourceId,
        fileId: created.fileId,
        fileRevisionId: created.fileRevisionId,
        configId: newConfig.id,
        snapshotId: newSnapshotId,
      };
    },
  });
}
